import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import { HelpBadge } from '../../shared/HelpBadge';
import {
    STOREFRONT_STATUS_TOOLTIP,
    StorefrontStatusText,
} from '../../shared/StorefrontStatusBadge';
import { SortIcon } from '../../shared/index';
import { UserRoles } from '@lifeserver/shared';

import type { IapProduct } from '../../hooks/iapProducts';
import { useIsAuthorized } from '../../hooks/useIsAuthorized';

import { formatDate } from '../../utils/dates';
import type { SkuSortByType } from './utils';

import { RouteTo } from '../../routes';

import styles from './IapProductsTable.module.scss';

const GOOGLE_STORE = 'google';
const APPLE_STORE = 'apple';
const SYNCABLE_STORES = new Set<string>([GOOGLE_STORE, APPLE_STORE]);

const STOREFRONT_NAMES: Record<string, string> = {
    [GOOGLE_STORE]: 'Google Play',
    [APPLE_STORE]: 'App Store',
};

const ACTIVE_TOOLTIP =
    'Whether the SKU is offered to players. Only active SKUs are returned by ' +
    'the game-facing API; inactive ones are kept for history but are not sold. ' +
    'This flag is edited on the Pack page, not here.';

type IapProductsTableProps = {
    products: IapProduct[];
    onSync: (sku: string, store: string) => void;
    syncingKey: string | null;
    sortBy: SkuSortByType | undefined;
    isSortAscend: boolean;
    onClickSort: (sortBy: SkuSortByType) => void;
};

function SortableHeader({
    label,
    sortBy,
    activeSortBy,
    isSortAscend,
    onClickSort,
    alignRight,
    tooltip,
}: {
    label: string;
    sortBy: SkuSortByType;
    activeSortBy: SkuSortByType | undefined;
    isSortAscend: boolean;
    onClickSort: (sortBy: SkuSortByType) => void;
    alignRight?: boolean;
    tooltip?: string;
}) {
    return (
        <th>
            <div
                className="sortable"
                style={alignRight ? { justifyContent: 'flex-end' } : undefined}
                onClick={() => onClickSort(sortBy)}
            >
                {label}
                {tooltip && <HelpBadge title={tooltip} ariaLabel={label} />}
                <SortIcon
                    isActive={activeSortBy === sortBy}
                    isSortAscend={isSortAscend}
                />
            </div>
        </th>
    );
}

function DetailField({
    label,
    value,
    onValueClick,
}: {
    label: string;
    value: string;
    onValueClick?: () => void;
}) {
    return (
        <div style={{ minWidth: 160, maxWidth: 320, marginRight: 24 }}>
            <div className="labelInput">
                <label>{label}</label>
            </div>
            <div style={{ overflowWrap: 'anywhere' }}>
                {onValueClick ? (
                    <button
                        className="link"
                        onClick={(e) => {
                            e.stopPropagation();
                            onValueClick();
                        }}
                    >
                        {value}
                    </button>
                ) : (
                    value
                )}
            </div>
        </div>
    );
}

export function IapProductsTable({
    products,
    onSync,
    syncingKey,
    sortBy,
    isSortAscend,
    onClickSort,
}: IapProductsTableProps) {
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const { isAuthorized } = useIsAuthorized(UserRoles.PriceAutomationWrite);
    const navigate = useNavigate();

    const rowKey = (product: IapProduct) => `${product.store}:${product.sku}`;

    const toggleRow = (product: IapProduct) => {
        const key = rowKey(product);
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <table
            className={styles.table}
            style={{ width: '100%', tableLayout: 'fixed' }}
        >
            <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '36%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '22%' }} />
            </colgroup>
            <thead>
                <tr>
                    <th />
                    <SortableHeader
                        label="SKU"
                        sortBy="sku"
                        activeSortBy={sortBy}
                        isSortAscend={isSortAscend}
                        onClickSort={onClickSort}
                    />
                    <SortableHeader
                        label="Store"
                        sortBy="store"
                        activeSortBy={sortBy}
                        isSortAscend={isSortAscend}
                        onClickSort={onClickSort}
                    />
                    <SortableHeader
                        label="Active"
                        sortBy="active"
                        activeSortBy={sortBy}
                        isSortAscend={isSortAscend}
                        onClickSort={onClickSort}
                        tooltip={ACTIVE_TOOLTIP}
                    />
                    <SortableHeader
                        label="Storefront status"
                        sortBy="storefrontStatus"
                        activeSortBy={sortBy}
                        isSortAscend={isSortAscend}
                        onClickSort={onClickSort}
                        tooltip={STOREFRONT_STATUS_TOOLTIP}
                    />
                    <th className="alignRight">Actions</th>
                </tr>
            </thead>
            <tbody>
                {products.map((product) => {
                    const key = rowKey(product);
                    const isOpen = expanded.has(key);
                    const isSyncable = SYNCABLE_STORES.has(product.store);
                    const isSyncing = syncingKey === key;

                    return (
                        <Fragment key={key}>
                            <tr onClick={() => toggleRow(product)}>
                                <td>
                                    <button
                                        aria-label="expand"
                                        className="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRow(product);
                                        }}
                                    >
                                        {isOpen ? '▲' : '▼'}
                                    </button>
                                </td>
                                <td
                                    className="alignLeft"
                                    style={{ overflowWrap: 'anywhere' }}
                                >
                                    {product.sku}
                                </td>
                                <td className="alignLeft">{product.store}</td>
                                <td
                                    className={classNames(
                                        'alignLeft',
                                        !product.active && 'inactive',
                                    )}
                                >
                                    {product.active ? 'Yes' : 'No'}
                                </td>
                                <td className="alignLeft">
                                    <StorefrontStatusText
                                        status={product.storefrontStatus}
                                    />
                                </td>
                                <td
                                    className="alignRight"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {isSyncable && isAuthorized && (
                                        <button
                                            className="small"
                                            disabled={isSyncing}
                                            onClick={() =>
                                                onSync(
                                                    product.sku,
                                                    product.store,
                                                )
                                            }
                                        >
                                            {isSyncing
                                                ? 'Syncing...'
                                                : `Sync with ${STOREFRONT_NAMES[product.store]}`}
                                        </button>
                                    )}
                                </td>
                            </tr>
                            <tr
                                className="no-highlight"
                                style={{
                                    display: isOpen ? 'table-row' : 'none',
                                }}
                            >
                                <td colSpan={6} style={{ padding: 0 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            padding: 12,
                                        }}
                                    >
                                        <DetailField
                                            label="ID"
                                            value={
                                                product.id != null
                                                    ? String(product.id)
                                                    : '—'
                                            }
                                        />
                                        <DetailField
                                            label="SKU"
                                            value={product.sku}
                                        />
                                        <DetailField
                                            label="Pack ID"
                                            value={product.packId || '—'}
                                            onValueClick={
                                                product.packId
                                                    ? () =>
                                                          navigate(
                                                              RouteTo.PackEdit(
                                                                  product.packId,
                                                              ),
                                                          )
                                                    : undefined
                                            }
                                        />
                                        <DetailField
                                            label="Type"
                                            value={product.type ?? '—'}
                                        />
                                        <DetailField
                                            label="Price"
                                            value={
                                                product.price === null
                                                    ? '—'
                                                    : String(product.price)
                                            }
                                        />
                                        <DetailField
                                            label="Created"
                                            value={formatDate(product.created)}
                                        />
                                        <DetailField
                                            label="Last modified"
                                            value={formatDate(
                                                product.lastModified,
                                            )}
                                        />
                                    </div>
                                </td>
                            </tr>
                        </Fragment>
                    );
                })}
            </tbody>
        </table>
    );
}
